import { NavLink, useLocation } from "react-router-dom-v5-compat";
import {
  IconNameEnum,
  useModuleBEM,
  usePackagesStyles,
} from "@b2bportal/core-themes";
import {
  CorePackagesComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TranslationLanguage } from "@hopper-b2b/types";
import { getPackageRoutes } from "@overrides/utilities";
import { DateRangePicker } from "@tenant/hotels";
import { PackageSelectRadioGroup } from "@tenant/packages";
import { Icon, InputNumber } from "@tenant/ui";
import clsx from "clsx";
import { PackageTravellerPicker } from "../PackageTravellerPicker";
import { usePackageSearch } from "./hooks";
import defaultStyles from "./PackageSearch.module.scss";
import { PackageLocationAutocomplete } from "@/pages/Packages/components";

export type PackageSearchProps = {
  onSubmit?: () => void;
  onSubmitAppend?: () => void;
};
export type PackageSearchComponentProps = PlatformComponentProps &
  PackageSearchProps;

const COMPONENT_KEY = CorePackagesComponent.PackagesSearch;

export const PackageSearch = ({
  children,
  className,
  ...rest
}: PackageSearchComponentProps) => {
  const styles = usePackagesStyles(COMPONENT_KEY, defaultStyles);
  const { t, language } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { context, handlers } = usePackageSearch(rest);

  const { BASE_ROUTE: PACKAGES_BASE_ROUTE, FULL_ROUTES: PACKAGES_FULL_ROUTES } =
    getPackageRoutes();
  const { pathname } = useLocation();
  const isPackageSearchRoute = pathname === PACKAGES_FULL_ROUTES.SEARCH;

  return (
    <div className={clsx(block, className)}>
      {isPackageSearchRoute ? (
        <div className={cn("header")}>
          <h1 className={cn("header-title")}>
            {t("core-packages.search.startSearch")}
          </h1>
          <NavLink className={cn("header-close")} to={PACKAGES_BASE_ROUTE}>
            <Icon iconName={IconNameEnum.close} useCurrentColor={false} />
          </NavLink>
        </div>
      ) : null}
      {context.shouldShowPackageType !== undefined &&
        Boolean(context.shouldShowPackageType) && (
          <PackageSelectRadioGroup
            onSelectValue={handlers.handlePackageTypeChange}
            selectedOption={context.packageType}
          />
        )}
      <form
        className={cn("form", {
          ["fr"]:
            language === TranslationLanguage["fr-CA"] ||
            language === TranslationLanguage.fr,
        })}
        onSubmit={handlers.onFormSubmit}
      >
        <div className={cn("location", ["origin"])}>
          <PackageLocationAutocomplete
            id="package-location-autocomplete-origin"
            label={context.originLabel}
            value={context.origin}
            onChange={handlers.handleOriginChange}
            onFocus={handlers.handleOriginFocus}
            placeholder={context.originPlaceholder}
            error={context.formError?.origin}
          />
        </div>
        {context.shouldShowDestinationSelection && (
          <div className={cn("location", ["destination"])}>
            <PackageLocationAutocomplete
              id="package-location-autocomplete-destination"
              label={context.destinationLabel}
              value={context.destination}
              onChange={handlers.handleDestinationChange}
              onFocus={handlers.handleDestinationFocus}
              placeholder={context.destinationPlaceholder}
              error={context.formError?.destination}
            />
          </div>
        )}
        <div className={cn("dates")}>
          <DateRangePicker
            startDate={context.dateRange?.from}
            endDate={context.dateRange?.until}
            onStartDateChangeHandler={handlers.handleDateRangeFromChange}
            onEndDateChangeHandler={handlers.handleDateRangeUntilChange}
            dialogTitle={t("core-hotels.search.date.mobileTitle")}
            error={context.formError?.dateRange}
          />
        </div>
        <div className={cn("guest")}>
          <PackageTravellerPicker
            guests={context.guests}
            onGuestsChange={handlers.handleGuestsChange}
            showInfants={context.packageTypeIncludesFlights}
          />
        </div>
        {context.shouldShowDriverAge && (
          <div className={cn("driver-age")}>
            <InputNumber
              startContent={<Icon iconName={IconNameEnum.guest} />}
              label={context.driverAgeLabel}
              value={context.driverAge ?? ""}
              onChange={(e) => handlers.handleDriverAgeChange(e.target.value)}
              placeholder={context.driverAgePlaceholder}
            />
          </div>
        )}
        <button className={cn("search-button")} type="submit">
          {context.searchButtonLabel}
        </button>
        {children}
      </form>
    </div>
  );
};
